/**
 * Pointer recording for draw-to-pattern.
 *
 * Uses POINTER LOCK and `movementX`/`movementY`, never `clientX`/`clientY`.
 *
 * That is not a stylistic preference. Without pointer lock the cursor stops at the
 * screen edge and the browser keeps reporting the same clamped coordinate, so a
 * stroke that runs off the edge is silently truncated -- you draw a long diagonal
 * and get back the first half of it. Under lock, `movementX`/`movementY` are raw
 * device deltas with no clamping, which is also exactly the form the HID mouse
 * report wants.
 *
 * Timestamps come from `event.timeStamp`, which shares its time origin with
 * `performance.now()`. `Date.now()` would quantise to milliseconds and make the
 * trace lumpy.
 *
 * ## Who starts and who stops the recording
 *
 * **`capture()` starts it. The `pointerlockchange` handler only stops it.**
 *
 * That asymmetry is deliberate. Driving the START off the lock event meant a missed
 * or out-of-order event left the UI idle with the cursor hidden, and -- combined with
 * a lock check that compared two nullable values -- made Esc *start* a recording. The
 * start is now an explicit step at the end of `capture()`, where the outcome is known
 * because it is read back from `document.pointerLockElement` rather than assumed from
 * a promise. The handler keeps the one job it can do safely: end a run that lost its
 * lock.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import { acquirePointerLock, isOurLockHeld } from './pointerLock.ts';
import type { TraceSample } from './resample.ts';

export type RecorderPhase = 'idle' | 'recording' | 'error';

/** Refuse to grow without bound if someone leaves it recording. */
const MAX_SAMPLES = 120_000;

/**
 * Why a refusal happened, as far as it can be known.
 *
 * The `pointerlockerror` event carries NO reason, so when a refusal is reported from
 * that event rather than from a rejected promise, the message has to name the likely
 * cause. The likely cause is specific here, and it is the app's own fault:
 *
 *   "If calling requestPointerLock() immediately after releasing the pointer lock via
 *    the default unlock gesture (instead of through an exitPointerLock() call), the
 *    call will fail, even if a transient activation is available."
 *                                        -- MDN, Element.requestPointerLock() Security
 *
 * The "default unlock gesture" is Esc. A programmatic `exitPointerLock()` has no such
 * restriction. Space stops programmatically; mouse clicks belong to the trace.
 */
function lockRefusalMessage(reason: string | null, msSinceEscRelease: number | null): string {
  const parts = ['The browser refused to lock the pointer.'];
  if (reason) parts.push(`It reported: “${reason}”.`);

  if (msSinceEscRelease !== null && msSinceEscRelease < ESC_COOLDOWN_MS) {
    parts.push(
      `Esc released the pointer ${(msSinceEscRelease / 1000).toFixed(1)} s ago, and the browser blocks a new lock for a moment after that. Wait a second and click again.`,
    );
  } else {
    parts.push(
      'That usually means the pointer was released with Esc a moment ago, or that this tab is not focused.',
    );
  }

  parts.push('Stopping a recording with the space bar avoids this cooldown.');
  return parts.join(' ');
}

/**
 * How long a release by Esc is assumed to block a new lock.
 *
 * The spec says the restriction exists but does not give a duration, so this is a
 * judgement: long enough to cover Chromium's behaviour, short enough that the advice
 * to "try again" is not a long wait.
 */
const ESC_COOLDOWN_MS = 2000;

export interface PointerRecorder {
  phase: RecorderPhase;
  /** The FINALISED trace. Empty while recording — see `sampleCount` for the live value. */
  samples: readonly TraceSample[];
  /**
   * Live number of samples captured so far.
   *
   * Separate from `samples` because that array is only published when a run ends, so
   * the on-screen counter read 0 for the whole recording -- which is indistinguishable
   * from a recording that is silently capturing nothing.
   */
  sampleCount: number;
  /** Live elapsed time, refreshed while recording so the UI can count up. */
  elapsedMs: number;
  error: string | null;
  isLocked: boolean;
  /**
   * Acquire the lock and begin recording.
   *
   * `beforeStart` runs AFTER the lock is held but BEFORE the clock starts. That
   * ordering matters: the lock request needs a user gesture, so anything awaited
   * before it can push the request outside the gesture window and have it refused.
   * Parking the device is therefore done after the lock, and `startInternal()`
   * clears the buffer afterwards so nothing captured while the device was still
   * moving survives into the trace.
   */
  capture: (target: HTMLElement, beforeStart?: () => Promise<void>) => Promise<void>;
  stop: () => void;
  discard: () => void;
}

export function usePointerRecorder(): PointerRecorder {
  const [phase, setPhase] = useState<RecorderPhase>('idle');
  const [samples, setSamples] = useState<TraceSample[]>([]);
  const [sampleCount, setSampleCount] = useState(0);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isLocked, setIsLocked] = useState(false);

  /** The element we asked the browser to lock. Must be set BEFORE requesting. */
  const targetRef = useRef<HTMLElement | null>(null);
  const originRef = useRef(0);
  const lastRef = useRef(0);
  const bufferRef = useRef<TraceSample[]>([]);
  const recordingRef = useRef(false);
  /** A `capture()` is in flight; the lock handler must not second-guess it. */
  const startingRef = useRef(false);
  /**
   * Whether to keep asking for `unadjustedMovement`.
   *
   * Cleared the first time a grant comes back without it, so a platform that refuses
   * the option pays for the probe once rather than on every single recording.
   */
  const rawMovementRef = useRef(true);
  /** `capture()` already reported this failure, with a better reason than the event. */
  const reportedRef = useRef(false);
  /**
   * When the pointer was last released by the browser's OWN unlock gesture (Esc),
   * as opposed to by our `exitPointerLock()`.
   *
   * The distinction decides whether a refusal is our bug or an unavoidable browser
   * rule, and it is only observable in the `pointerlockchange` handler: by the time
   * our own `exitPointerLock()` fires that event, the run has already been stopped.
   */
  const escReleaseRef = useRef<number | null>(null);

  /** Milliseconds since an Esc release, or null if there has not been one. */
  const elapsedSinceEscRelease = useCallback((): number | null => {
    const at = escReleaseRef.current;
    return at === null ? null : performance.now() - at;
  }, []);

  /* ------------------------------------------------------------ lifecycle -- */

  const releaseLock = useCallback(() => {
    if (isOurLockHeld(targetRef.current, document.pointerLockElement)) {
      document.exitPointerLock();
    }
  }, []);

  const failInternal = useCallback(
    (message: string) => {
      recordingRef.current = false;
      startingRef.current = false;
      releaseLock();
      targetRef.current = null;
      setIsLocked(false);
      setPhase('error');
      setError(message);
      bufferRef.current = [];
      setSamples([]);
      setSampleCount(0);
    },
    [releaseLock],
  );

  // Declared before the handlers that call it: they only run at event time, but
  // referencing it from above would still be a block-scoped use-before-declaration.
  const stopInternal = useCallback(() => {
    // Preserve the final pause and release any held buttons, even on focus loss.
    if (recordingRef.current) {
      const t = Math.max(lastRef.current, performance.now() - originRef.current);
      bufferRef.current.push({ t, dx: 0, dy: 0, buttons: 0 });
      setElapsedMs(t);
    }
    recordingRef.current = false;
    startingRef.current = false;
    setSamples([...bufferRef.current]);
    setSampleCount(bufferRef.current.length);
    setPhase('idle');
    releaseLock();
    targetRef.current = null;
    setIsLocked(false);
  }, [releaseLock]);

  const startInternal = useCallback(() => {
    if (recordingRef.current) return;
    recordingRef.current = true;
    startingRef.current = false;
    // Cleared here rather than in `capture()`: everything buffered before this point
    // may include the device's own motion while it was being parked.
    bufferRef.current = [];
    originRef.current = performance.now();
    lastRef.current = 0;
    setError(null);
    setSamples([]);
    setSampleCount(0);
    setElapsedMs(0);
    setPhase('recording');
  }, []);

  /* ------------------------------------------------------------- handlers -- */

  const onPointerMove = useCallback(
    (event: PointerEvent) => {
      if (!recordingRef.current) return;

      // `getCoalescedEvents` returns every input sample the browser merged into this
      // one event. Without it a fast stroke loses detail, because the browser
      // delivers one event per animation frame rather than per device report.
      const events =
        typeof event.getCoalescedEvents === 'function' ? event.getCoalescedEvents() : [];
      const list = events.length > 0 ? events : [event];

      for (const e of list) {
        if (!recordingRef.current) return;
        const t = e.timeStamp - originRef.current;
        // Guard against a non-monotonic timestamp ending up as a negative bucket.
        const clamped = t < lastRef.current ? lastRef.current : t;
        lastRef.current = clamped;

        bufferRef.current.push({ t: clamped, dx: e.movementX || 0, dy: e.movementY || 0, buttons: e.buttons & 7 });
        if (bufferRef.current.length >= MAX_SAMPLES) {
          failInternal('Recording reached the sample limit and was stopped.');
          return;
        }
      }

      // Published per event rather than per coalesced sample: the counter should be
      // live, and one state update per frame is cheap. `bufferRef` alone cannot drive
      // the UI, because mutating a ref does not re-render.
      setSampleCount(bufferRef.current.length);
    },
    [failInternal],
  );

  // Mouse down/up fire for EVERY button change, including chords; pointer down/up do not.
  const onButton = useCallback((event: MouseEvent) => {
    if (!recordingRef.current) return;
    event.preventDefault();
    const t = Math.max(lastRef.current, event.timeStamp - originRef.current);
    lastRef.current = t;
    bufferRef.current.push({ t, dx: 0, dy: 0, buttons: event.buttons & 7 });
    if (bufferRef.current.length >= MAX_SAMPLES) {
      failInternal('Recording reached the sample limit. Record a shorter sequence.');
      return;
    }
    setSampleCount(bufferRef.current.length);
  }, [failInternal]);

  const onContextMenu = useCallback((event: MouseEvent) => {
    if (recordingRef.current) event.preventDefault();
  }, []);

  const onPointerLockChange = useCallback(() => {
    const held = isOurLockHeld(targetRef.current, document.pointerLockElement);
    setIsLocked(held);

    // ONLY the stop direction. The start is driven by `capture()`, which knows the
    // outcome of the request it made; reacting to this event to start is what made
    // Esc begin a recording instead of ending one.
    if (!held && recordingRef.current) {
      // Still recording means WE did not release it -- `stopInternal()` clears the
      // flag before calling `exitPointerLock()`. So this is the browser's own unlock
      // gesture (Esc) or a focus change, and either way a new lock will be refused
      // for a moment. Record when, so the refusal message can be specific.
      escReleaseRef.current = performance.now();
      stopInternal();
      return;
    }
    // Nothing held and no request in flight: the target is stale, so drop it. Leaving
    // it set would let a later comparison match an element that is not locked.
    if (!held && !startingRef.current) targetRef.current = null;
  }, [stopInternal]);

  /**
   * Space is the dedicated stop key. Mouse clicks belong to the recording.
   *
   * Esc is the browser's default unlock gesture, and a lock request that follows it
   * fails even with a fresh click. Offering a key that goes through
   * `exitPointerLock()` instead means the user never has to trip that rule.
   */
  const onKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!recordingRef.current) return;
      if (event.key !== ' ') return;
      event.preventDefault(); // space would otherwise scroll the page
      stopInternal();
    },
    [stopInternal],
  );

  const onPointerLockError = useCallback(() => {
    // Ignore the event in every case where it is not the whole story.
    //
    //  - we hold the lock, or are recording: the error belongs to a request that a
    //    later attempt superseded, and reporting it would blame the user for a lock
    //    we are actively holding.
    //  - `capture()` has a request in flight, or has just reported one: it has the
    //    promise's rejection reason, which this event does not carry, so its message
    //    is strictly better. Overwriting it here would replace the cause with "it
    //    was refused".
    if (recordingRef.current) return;
    if (isOurLockHeld(targetRef.current, document.pointerLockElement)) return;
    if (startingRef.current || reportedRef.current) return;

    failInternal(lockRefusalMessage(null, elapsedSinceEscRelease()));
  }, [failInternal, elapsedSinceEscRelease]);

  const stop = useCallback(() => {
    if (recordingRef.current) stopInternal();
  }, [stopInternal]);

  const discard = useCallback(() => {
    recordingRef.current = false;
    startingRef.current = false;
    bufferRef.current = [];
    setSamples([]);
    setSampleCount(0);
    setElapsedMs(0);
    setError(null);
    setPhase('idle');
    releaseLock();
    targetRef.current = null;
    setIsLocked(false);
  }, [releaseLock]);

  const capture = useCallback(
    async (target: HTMLElement, beforeStart?: () => Promise<void>) => {
      if (startingRef.current || recordingRef.current) return;
      if (typeof target.requestPointerLock !== 'function') {
        failInternal('This browser does not support Pointer Lock.');
        return;
      }

      setError(null);
      setSamples([]);
      setElapsedMs(0);
      reportedRef.current = false;

      // Set BEFORE requesting. Both the lock handler and `releaseLock()` compare
      // against this, and leaving it null made that comparison `null === null` --
      // true -- whenever nothing was locked.
      targetRef.current = target;
      startingRef.current = true;

      const result = await acquirePointerLock(target, { tryUnadjusted: rawMovementRef.current });
      if (!startingRef.current || !target.isConnected) {
        if (document.pointerLockElement === target) document.exitPointerLock();
        return;
      }

      // A grant that did NOT use `unadjustedMovement` means the platform refused the
      // option. Stop asking, so the rejection is not re-paid on every recording.
      if (result.granted && !result.unadjusted) rawMovementRef.current = false;

      if (!result.granted || document.pointerLockElement !== target) {
        startingRef.current = false;
        reportedRef.current = true;
        failInternal(lockRefusalMessage(result.reason, elapsedSinceEscRelease()));
        return;
      }

      setIsLocked(true);

      // After the lock, deliberately: the lock request needs the user gesture, so
      // awaiting anything before it risks having the request refused. Nothing
      // buffered during this window survives -- `startInternal()` clears it.
      if (beforeStart) {
        try {
          await beforeStart();
        } catch (e) {
          failInternal(e instanceof Error ? e.message : 'Could not park the device.');
          return;
        }
      }

      // The lock can be lost while we were parking (Esc, or the tab losing focus).
      // The lock handler has already finished the run in that case; do not restart it.
      if (!startingRef.current || !target.isConnected || document.pointerLockElement !== target) {
        releaseLock();
        startingRef.current = false;
        targetRef.current = null;
        setIsLocked(false);
        return;
      }

      startInternal();
    },
    [failInternal, startInternal, elapsedSinceEscRelease, releaseLock],
  );

  /* ---------------------------------------------------------------- wiring -- */

  useEffect(() => {
    document.addEventListener('pointerlockchange', onPointerLockChange);
    document.addEventListener('pointerlockerror', onPointerLockError);
    document.addEventListener('pointermove', onPointerMove);
    document.addEventListener('mousedown', onButton);
    document.addEventListener('mouseup', onButton);
    document.addEventListener('contextmenu', onContextMenu);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerlockchange', onPointerLockChange);
      document.removeEventListener('pointerlockerror', onPointerLockError);
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('mousedown', onButton);
      document.removeEventListener('mouseup', onButton);
      document.removeEventListener('contextmenu', onContextMenu);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onPointerLockChange, onPointerLockError, onPointerMove, onButton, onContextMenu, onKeyDown]);

  useEffect(() => {
    if (phase !== 'recording') return;
    const id = setInterval(() => setElapsedMs(performance.now() - originRef.current), 100);
    return () => clearInterval(id);
  }, [phase]);

  useEffect(() => {
    return () => {
      recordingRef.current = false;
      startingRef.current = false;
      releaseLock();
      targetRef.current = null;
    };
  }, [releaseLock]);

  return { phase, samples, sampleCount, elapsedMs, error, isLocked, capture, stop, discard };
}
